import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useVAGovt } from "@/lib/hooks";
import { fmtInt, fmtUSDCompact, fmtDelta, deltaTone } from "@/lib/format";
import { downloadXlsx, stampedName } from "@/lib/exportXlsx";
import DownloadButton from "@/components/DownloadButton";
import type { VAGovtHospital } from "@/lib/api";

type Metric  = "qty" | "revenue";
type SortKey = "name" | "idn" | "since" | "monitors" | "yoy" | string; // string = year
type SortDir = "asc" | "desc";

function valFor(h: VAGovtHospital, year: number, metric: Metric): number {
  const row = h.yearly.find((y) => y.year === year);
  return row ? (metric === "revenue" ? row.revenue : row.qty) : 0;
}
function ytdCur(h: VAGovtHospital, years: number[], metric: Metric): number {
  const cur = years[years.length - 1];
  return cur != null ? valFor(h, cur, metric) : 0;
}
function ytdPrior(h: VAGovtHospital, years: number[], metric: Metric): number {
  const prior = years[years.length - 2];
  return prior != null ? valFor(h, prior, metric) : 0;
}
function yoyPct(h: VAGovtHospital, years: number[], metric: Metric): number | null {
  const p = ytdPrior(h, years, metric);
  const c = ytdCur(h, years, metric);
  return p > 0 ? ((c - p) / p) * 100 : null;
}

export default function VAGovt() {
  const [metric,  setMetric]  = useState<Metric>("qty");
  const [groupBy, setGroupBy] = useState<"none" | "idn">("idn");
  const [sortKey, setSortKey] = useState<SortKey>("since");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const { data, isLoading, error } = useVAGovt();

  const years       = data?.years ?? [];
  const hospitals   = data?.hospitals ?? [];
  const fmt         = metric === "revenue" ? fmtUSDCompact : fmtInt;

  const sorted = useMemo(() => {
    const list = [...hospitals];
    list.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortKey === "name") {
        av = a.customer_name.toLowerCase();
        bv = b.customer_name.toLowerCase();
      } else if (sortKey === "idn") {
        av = (a.idn_network ?? "").toLowerCase();
        bv = (b.idn_network ?? "").toLowerCase();
      } else if (sortKey === "since") {
        av = a.customer_since ?? "";
        bv = b.customer_since ?? "";
      } else if (sortKey === "monitors") {
        av = a.monitor_count;
        bv = b.monitor_count;
      } else if (sortKey === "yoy") {
        av = yoyPct(a, years, metric) ?? -Infinity;
        bv = yoyPct(b, years, metric) ?? -Infinity;
      } else {
        av = valFor(a, Number(sortKey), metric);
        bv = valFor(b, Number(sortKey), metric);
      }
      const cmp = typeof av === "string" ? av.localeCompare(bv as string) : av - (bv as number);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitals, metric, sortKey, sortDir, years]);

  // Group by IDN when enabled
  const groups: { label: string; rows: VAGovtHospital[] }[] = useMemo(() => {
    if (groupBy === "none") return [{ label: "", rows: sorted }];
    const map = new Map<string, VAGovtHospital[]>();
    for (const h of sorted) {
      const key = h.idn_network ?? "Other";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(h);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, rows]) => ({ label, rows }));
  }, [sorted, groupBy]);

  const totals = useMemo(() => {
    const perYear: Record<number, number> = {};
    let totalMonitors = 0;
    for (const h of hospitals) {
      totalMonitors += h.monitor_count;
      for (const y of years) perYear[y] = (perYear[y] ?? 0) + valFor(h, y, metric);
    }
    const cur   = perYear[years[years.length - 1]] ?? 0;
    const prior = perYear[years[years.length - 2]] ?? 0;
    const yoy   = prior > 0 ? ((cur - prior) / prior) * 100 : null;
    return { perYear, yoy, totalMonitors };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitals, years, metric]);

  const setSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" || key === "idn" || key === "since" ? "asc" : "desc");
    }
  };
  const arrow = (key: SortKey) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  function exportXlsx() {
    const headers = [
      "Account", "IDN / Network", "Category", "Address", "State",
      "Customer Since", "Monitors",
      ...years.map((y) => `${y} ${metric}`),
      "YoY %",
    ];
    const body = sorted.map((h) => [
      h.customer_name,
      h.idn_network ?? "",
      h.hospital_category ?? "",
      h.street_address ?? "",
      h.state ?? "",
      h.customer_since ?? "",
      h.monitor_count,
      ...years.map((y) => valFor(h, y, metric) || ""),
      yoyPct(h, years, metric) ?? "",
    ]);
    body.push([
      "TOTAL", "", "", "", "", "",
      totals.totalMonitors,
      ...years.map((y) => totals.perYear[y] ?? 0),
      totals.yoy ?? "",
    ]);
    downloadXlsx(stampedName("va-govt-catheter-orders"), headers, body);
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">VA / Government Accounts</h1>
          <p className="mt-1 text-sm text-ink-500">
            Catheter order history by IDN/network for VA and government hospital sites.
            {!isLoading && hospitals.length > 0 && (
              <> {hospitals.length} sites · {totals.totalMonitors} monitors total.</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Toggle
            value={groupBy}
            onChange={(v) => setGroupBy(v as "none" | "idn")}
            options={[
              { value: "idn", label: "Group by IDN" },
              { value: "none", label: "Flat list" },
            ]}
          />
          <Toggle
            value={metric}
            onChange={(v) => setMetric(v as Metric)}
            options={[
              { value: "qty", label: "Quantity" },
              { value: "revenue", label: "Revenue" },
            ]}
          />
          <DownloadButton onClick={exportXlsx} disabled={isLoading || !hospitals.length} />
        </div>
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">{(error as Error).message}</div>
      )}

      <section className="card overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-10 text-center text-sm text-ink-500">Loading…</div>
          ) : hospitals.length === 0 ? (
            <div className="p-10 text-center text-sm text-ink-500">
              No VA / government hospitals found. Run <code>db/va_setup.py</code> to import hospital metadata.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <Th onClick={() => setSort("name")}     align="left">Account{arrow("name")}</Th>
                  <Th onClick={() => setSort("idn")}      align="left">IDN / Network{arrow("idn")}</Th>
                  <ThStatic align="left">Address</ThStatic>
                  <ThStatic align="left">State</ThStatic>
                  <Th onClick={() => setSort("since")}    align="left">Since{arrow("since")}</Th>
                  <Th onClick={() => setSort("monitors")} align="right">Monitors{arrow("monitors")}</Th>
                  {years.map((y) => (
                    <Th key={y} onClick={() => setSort(String(y))} align="right">
                      {y}{arrow(String(y))}
                    </Th>
                  ))}
                  <Th onClick={() => setSort("yoy")} align="right">YoY{arrow("yoy")}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {groups.map(({ label, rows }) => (
                  <>
                    {groupBy === "idn" && label && (
                      <tr key={`group-${label}`} className="bg-ink-50">
                        <td
                          colSpan={6 + years.length + 1}
                          className="px-5 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink-500"
                        >
                          {label}
                          <span className="ml-2 font-normal normal-case">
                            {rows.length} site{rows.length !== 1 ? "s" : ""} ·{" "}
                            {rows.reduce((s, h) => s + h.monitor_count, 0)} monitors
                          </span>
                        </td>
                      </tr>
                    )}
                    {rows.map((h) => {
                      const pct  = yoyPct(h, years, metric);
                      const tone = deltaTone(pct);
                      return (
                        <tr key={h.customer_id} className="hover:bg-ink-50">
                          <td className="px-5 py-2 font-medium text-ink-900">
                            <Link
                              to={`/customers/${encodeURIComponent(h.customer_id)}`}
                              className="hover:text-accent-600"
                            >
                              {h.customer_name}
                            </Link>
                          </td>
                          <td className="px-5 py-2 text-ink-600">{h.idn_network ?? "—"}</td>
                          <td className="px-5 py-2 text-ink-600">{h.street_address ?? "—"}</td>
                          <td className="px-5 py-2 text-ink-600">{h.state ?? "—"}</td>
                          <td className="px-5 py-2 text-ink-600">
                            {h.customer_since ? h.customer_since.slice(0, 7) : "—"}
                          </td>
                          <td className="px-5 py-2 text-right tabular-nums text-ink-700">
                            {h.monitor_count || "—"}
                          </td>
                          {years.map((y) => {
                            const v = valFor(h, y, metric);
                            return (
                              <td
                                key={y}
                                className={`px-5 py-2 text-right tabular-nums ${
                                  v === 0 ? "text-ink-300" : "text-ink-900"
                                }`}
                              >
                                {v === 0 ? "—" : fmt(v)}
                              </td>
                            );
                          })}
                          <td className="px-5 py-2 text-right tabular-nums">
                            <span
                              className={
                                tone === "up" ? "text-success" : tone === "down" ? "text-danger" : "text-ink-500"
                              }
                            >
                              {fmtDelta(pct)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-ink-300 bg-ink-50 font-semibold">
                  <td className="px-5 py-2 text-ink-900" colSpan={5}>
                    All VA / Govt ({hospitals.length} sites)
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums text-ink-900">
                    {totals.totalMonitors}
                  </td>
                  {years.map((y) => (
                    <td key={y} className="px-5 py-2 text-right tabular-nums text-ink-900">
                      {totals.perYear[y] ? fmt(totals.perYear[y]) : "—"}
                    </td>
                  ))}
                  <td className="px-5 py-2 text-right tabular-nums">
                    <span
                      className={
                        deltaTone(totals.yoy) === "up"
                          ? "text-success"
                          : deltaTone(totals.yoy) === "down"
                            ? "text-danger"
                            : "text-ink-500"
                      }
                    >
                      {fmtDelta(totals.yoy)}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </section>

      {hospitals.length > 0 && (
        <p className="mt-3 text-xs text-ink-500">
          Catheters only (UOT, IAP, SmartFoley). Monitor counts use{" "}
          <code>GREATEST(orders_in_db, roster_override)</code> to fill gaps from pre-2023
          placements. Year columns are full-year totals.
        </p>
      )}
    </div>
  );
}

function ThStatic({ children, align }: { children: React.ReactNode; align: "left" | "right" }) {
  return (
    <th className={`px-5 py-2 font-medium uppercase tracking-wide text-ink-500 ${align === "left" ? "text-left" : "text-right"}`}>
      {children}
    </th>
  );
}

function Th({ children, onClick, align }: { children: React.ReactNode; onClick: () => void; align: "left" | "right" }) {
  return (
    <th className={`px-5 py-2 ${align === "left" ? "text-left" : "text-right"}`}>
      <button
        type="button"
        onClick={onClick}
        className="font-medium uppercase tracking-wide text-ink-500 hover:text-ink-900"
      >
        {children}
      </button>
    </th>
  );
}

function Toggle({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div className="flex gap-1.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition ${
            value === o.value
              ? "border-ink-900 bg-ink-900 text-white"
              : "border-ink-200 bg-white text-ink-600 hover:border-ink-300"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
